import {Inject, Injectable} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar, MatSnackBarRef, SimpleSnackBar} from '@angular/material/snack-bar';
import {BehaviorSubject, Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {ValidationResponseObj} from '../../../generated/generated-core.model';
import {
    MxConfirmDialogComponent
} from '../../modules/mx-dialogs/components/mx-confirm-dialog/mx-confirm-dialog.component';
import {
    MxErrorDialogComponent,
    MxErrorDialogData
} from '../../modules/mx-dialogs/components/mx-error-dialog/mx-error-dialog.component';
import {capitalize} from '../../utils/string-utils';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from './mx-translate.service';

export class NotificationObj {
    message: string;
    time: Date;
}

@Injectable({
    providedIn: 'root'
})
export class MxNotificationService {

    private STORAGE_KEY: string = 'recentMsgs';


    private errorDialogRef: MatDialogRef<MxErrorDialogComponent>;

    private errorDialogData: BehaviorSubject<MxErrorDialogData>;

    constructor(
            private snackBar: MatSnackBar,
            private dialog: MatDialog,
            @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService: MxTranslateService) {}

    private readFromStorage(): Array<NotificationObj> {
        let savedMsgs = sessionStorage.getItem(this.STORAGE_KEY);
        if (savedMsgs) {
            return JSON.parse(savedMsgs);
        } else {
            return [];
        }
    }

    private saveMessageToStorage(recentMsg: NotificationObj): void {
        let storage = this.readFromStorage();
        storage.push(recentMsg);
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage));
    }

    private saveInHistory(message: string): void {
        let notificationObj = new NotificationObj();
        notificationObj.time = new Date();
        notificationObj.message = message;
        this.saveMessageToStorage(notificationObj);
    }


    public showSuccess(message: string, saveInHistory: boolean = true, duration: number = 5000): MatSnackBarRef<SimpleSnackBar> {
        if (saveInHistory) {
            this.saveInHistory(message);
        }

        return this.snackBar.open(this.translateService.getTranslatedLabel(message), 'Ok', {
            duration: duration
        });
    }

    public showSuccessByObject(
        object: any,
        action: string,
        notificationMsg: (o: any) => string): MatSnackBarRef<SimpleSnackBar> {
        let msg = this.success(object, action, notificationMsg);
        return this.snackBar.open(msg, null, {
            duration: 5000
        });
    }

    private showRedirectionSuccessMessage(message: string, saveInHistory: boolean = true,
                                          duration: number = 10000): MatSnackBarRef<SimpleSnackBar>  {
        if (saveInHistory) {
            this.saveInHistory(message);
        }
        return this.snackBar.open(message + ' Do you want open it for edit?', 'Ok', {
            duration: duration
        });
    }

    public showFailure(errorMsg: string, saveInHistory: boolean = true): MatDialogRef<any> {
        if (saveInHistory) {
            this.saveInHistory(errorMsg);
        }
        return this.showErrorDialog('Error occurred', errorMsg, 'warn');
    }

    public showMessageDialog(title: string, message: string,
                             type: 'error' | 'warn' | 'accent' | 'info',
                             contentType?: 'json' | 'iframe' | 'html'): MatDialogRef<MxConfirmDialogComponent> {
        return this.dialog.open(MxConfirmDialogComponent, {
            panelClass: 'mx-dialog-panel-class',
            data: {
                title,
                message,
                type,
                isAlert: true,
                contentType: contentType == null ? 'html' : 'json'
            }
        });
    }

    public showErrorDialog(title: string, message: string,
                           type: 'error' | 'warn' | 'accent' | 'info'): MatDialogRef<MxErrorDialogComponent> {
        let data = {
            title,
            message,
            type
        };

        if (this.errorDialogRef) {
            this.errorDialogData.next(data);
        } else {
            this.errorDialogData = new BehaviorSubject<MxErrorDialogData>(data);
            this.errorDialogRef = this.dialog.open(MxErrorDialogComponent, {
                panelClass: 'mx-dialog-panel-class',
                data: this.errorDialogData
            });

            this.errorDialogRef.afterClosed().subscribe(() => {
                this.errorDialogRef = null;
                this.errorDialogData = null;
            });
        }
        return this.errorDialogRef;
    }

    public notify(
            obsrv$: Observable<any>,
            successMessage: string,
            errorMessagePrefix: string = 'Error occurred',
            redirectionFn: Function = null,
            successDuration: number = 5000,
            saveInHistory: boolean = true): Observable<any> {

        return obsrv$.pipe(
            tap(
                event => {
                    if (event && event.error) {
                        return;
                    }
                    if (event && redirectionFn) {
                        this.showRedirectionSuccessMessage(successMessage, saveInHistory)
                            .onAction().subscribe(() => redirectionFn(event));
                    } else {
                        this.showSuccess(successMessage, saveInHistory, successDuration);
                    }
                },
                error => {
                    let errorMsg = this.getErrorMessage(error, errorMessagePrefix);
                    this.showFailure(errorMsg, saveInHistory);
                }
            )
        );
    }

    public notifyByObject(
            obsrv$: Observable<any>,
            action: string,
            object: any,
            redirectionFn: (id: any) => void,
            notificationMsg: (o: any) => string): Observable<any> {
        let successMessage = this.success(object, action, notificationMsg);
        let errorMessagePrefix = this.error(object, action, notificationMsg);
        return this.notify(obsrv$, successMessage, errorMessagePrefix, redirectionFn);
    }

    public getAll(): NotificationObj[] {
        return this.readFromStorage();
    }

    public getErrorMessage(error: any, errorMessagePrefix: string =  'Error occurred'): string {
        let fromErrorMessage = error;
        let joinStr = ' : ';

        if (!error) {
        } else if (error.error && (error.error.fieldErrors || error.error.globalErrors) ) {
            fromErrorMessage = this.buildValidationErrorMessage(error.error);
            joinStr = '<br/><br/>';
        } else if (error.error && error.error.error) {
            fromErrorMessage = this.translateService ? this.translateService.getTranslatedLabel(error.error.error) : error.error.error;
        } else if (error.error && error.error.detail) {
            fromErrorMessage = this.translateService ? this.translateService.getTranslatedLabel(error.error.detail) : error.error.detail;
        } else if (error instanceof HttpErrorResponse) {
            fromErrorMessage = error.statusText;
        }

        let errorMsg = errorMessagePrefix;
        if (fromErrorMessage) {
            errorMsg = (!errorMsg || (errorMsg.length === 0))
                ? fromErrorMessage
                : errorMsg + joinStr + fromErrorMessage;
        }
        return errorMsg;
    }

    private buildValidationErrorMessage(error: ValidationResponseObj): string {
        let fieldValidationMessage: string = '';
        let globalErrorMessage: string = '';
        let validationMessage: string = '';
        let commaNewLine = ',<br/>&#09;<br/>';
        Object.keys(error.fieldErrors).map(key => error.fieldErrors[key]).map(validationArray => {
            fieldValidationMessage += validationArray
                    .map((value) => value.message)
                    .join(',<br/>&#09;')
                + '<br/>';
        });
        Object.keys(error.globalErrors).map(key => error.globalErrors[key]).map(validationArray => {
            if (validationArray.message.length > 0) {
                globalErrorMessage += validationArray.message += commaNewLine;
            }
        });
        if (globalErrorMessage.length > 0) {
            globalErrorMessage = globalErrorMessage.substr(0, globalErrorMessage.length - commaNewLine.length);
            validationMessage += '<b>Global errors:</b> ' + globalErrorMessage;
        }
        if (fieldValidationMessage.length > 0) {
            if (globalErrorMessage.length > 0) {
                validationMessage += commaNewLine;
            }
            validationMessage += '<b>Field validation:</b> ' + fieldValidationMessage;
        }
        return validationMessage;
    }

    public showFormValidationMessage(form: FormGroup): void {
        let errors = Object.entries(form.controls).reduce((previousValue, currentValue) => {
            if (currentValue[1].invalid) {
                previousValue  += Object.entries(currentValue[1].errors)
                    .reduce((prev, cur) => {return prev += '<b>' + currentValue[0] + '</b>: ' + cur[1].message; }, '') + '</br>';
            }
            return previousValue; } , '');
        this.dialog.open(MxConfirmDialogComponent, {
            panelClass: 'mx-dialog-panel-class',
            data : {
                type: 'warn',
                title: 'Validation failure',
                message: errors,
                isAlert: true
            }
        });
    }

    private success(o: any, action: string, notificationMsg?: (o: any) => string): string {
        const actionMsg = ` record was ${action}d`;
        let msg = notificationMsg ? notificationMsg(o) : actionMsg;
        if(msg.indexOf(' was ') < 0) {
            msg += actionMsg;
        }
        return `${capitalize(msg)}`;
    }

    private error(o: any, action: string, notificationMsg?: (o: any) => string): string {
        let msg = notificationMsg ? notificationMsg(o) : 'record';
        return `Error with ${action} ${msg}`;
    }
}
